import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Leaf, Info, TrendingUp, Award } from 'lucide-react';
import api from '@/lib/api';

/**
 * CO2Calculation Component
 * Shows CO2 storage potential and certificate value for a forest area
 */
export const CO2Calculation = ({ 
  flaeche_ha, 
  baumart = null,
  years = 10,
  compact = false 
}) => {
  const [calculation, setCalculation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCalculation = async () => {
      if (!flaeche_ha || flaeche_ha <= 0) {
        setCalculation(null);
        return;
      }

      setLoading(true);
      setError(null);
      
      try {
        const response = await api.post('/api/tools/co2-calculation', {
          flaeche_ha: parseFloat(flaeche_ha),
          baumart: baumart || null,
          years: years
        });
        setCalculation(response.data);
      } catch (err) {
        console.error('CO2 calculation error:', err);
        setError('CO₂-Berechnung nicht verfügbar');
        setCalculation(null);
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(fetchCalculation, 500);
    return () => clearTimeout(timeoutId);
  }, [flaeche_ha, baumart, years]);

  if (!flaeche_ha || flaeche_ha <= 0) {
    return null;
  }

  const formatNumber = (num) => {
    return new Intl.NumberFormat('de-DE').format(num);
  };

  const formatCurrency = (price) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  if (compact) {
    return (
      <div className="p-4 bg-green-50 border border-green-200 rounded-xl" data-testid="co2-calculation-compact">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-green-100">
            <Leaf className="h-5 w-5 text-green-600" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-green-900 mb-1">CO₂-Speicherpotenzial</p>
            {loading ? (
              <div className="animate-pulse h-6 w-32 bg-green-200 rounded"></div>
            ) : error ? (
              <p className="text-sm text-green-700">{error}</p>
            ) : calculation ? (
              <>
                <p className="text-lg font-bold text-green-900">
                  {formatNumber(calculation.total_storage_tons)} t CO₂
                </p>
                <p className="text-xs text-green-700 mt-1">
                  ca. {formatCurrency(calculation.estimated_certificate_value)} Zertifikatswert ({calculation.projection_years} Jahre)
                </p>
              </>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  return (
    <Card className="border-green-200 bg-green-50/50" data-testid="co2-calculation-full">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg text-green-900">
          <Leaf className="h-5 w-5" />
          CO₂-Klimaschutzpotenzial
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="space-y-2">
            <div className="animate-pulse h-8 w-48 bg-green-200 rounded"></div>
            <div className="animate-pulse h-4 w-32 bg-green-100 rounded"></div>
          </div>
        ) : error ? (
          <div className="flex items-center gap-2 text-green-700">
            <Info className="h-4 w-4" />
            <span>{error}</span>
          </div>
        ) : calculation ? (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-white/60 rounded-lg text-center">
                <Leaf className="h-6 w-6 text-green-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-green-900">
                  {formatNumber(calculation.total_storage_tons)}
                </p>
                <p className="text-xs text-green-700">Tonnen CO₂ in {calculation.projection_years} Jahren</p>
              </div>
              <div className="p-4 bg-white/60 rounded-lg text-center">
                <Award className="h-6 w-6 text-green-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-green-900">
                  {formatCurrency(calculation.estimated_certificate_value)}
                </p>
                <p className="text-xs text-green-700">geschätzter Zertifikatswert</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="p-3 bg-white/60 rounded-lg">
                <p className="text-green-600 text-xs mb-1">Jährliche Speicherung</p>
                <p className="font-semibold text-green-900">
                  {formatNumber(calculation.yearly_storage_tons)} t CO₂/Jahr
                </p>
              </div>
              <div className="p-3 bg-white/60 rounded-lg">
                <p className="text-green-600 text-xs mb-1">CO₂-Preis pro Tonne</p>
                <p className="font-semibold text-green-900">
                  {formatCurrency(calculation.certificate_price_per_ton)}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-2 p-3 bg-white/60 rounded-lg">
              <TrendingUp className="h-4 w-4 text-green-600 mt-0.5" />
              <div className="text-xs text-green-700">
                <p className="font-medium mb-1">Baumart: {calculation.baumart}</p>
                <p>Speicherrate: {calculation.storage_rate_per_ha_year} t CO₂/ha/Jahr</p>
              </div>
            </div>

            <div className="flex items-start gap-2 p-3 bg-green-100/50 rounded-lg border border-green-200">
              <Info className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-green-800">
                {calculation.disclaimer}
              </p>
            </div>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default CO2Calculation;
